import { Edit2, MapPin, Phone, Plus, Trash2, User } from "lucide-react";
import { Button } from "../../../components/ui";
import LocationModal from "./LocationModal";
import { useOfficeLocations } from "../hooks/useOfficeLocations";
import toast from "react-hot-toast";

export default function OfficeLocationsTab() {
  const {
    locations,
    loading,
    modalOpen,
    setModalOpen,
    editingLocation,
    openCreate,
    openEdit,
    removeLocation,
    saveLocation,
  } = useOfficeLocations();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h3 className="text-lg font-black text-slate-800 tracking-tight leading-none mb-2">
            Stores
          </h3>
          <p className="text-[13px] text-slate-500 font-medium">
            Manage your branch stores and work locations
          </p>
        </div>
        <Button
          onClick={() => {
            const currentUserData = JSON.parse(localStorage.getItem('company_user') || '{}');
            if (currentUserData.storeRef && currentUserData.storeRef !== 'all') {
              toast.error("Invalid! Switch To All Stores and try to add a new store.");
              return;
            }
            openCreate();
          }}
          variant="primary"
          size="md"
          leftIcon={<Plus size={16} />}
        >
          Store
        </Button>
      </div>

      {loading ? (
        <div className="py-12 text-center text-slate-400">Loading stores...</div>
      ) : locations.length === 0 ? (
        <div className="py-20 text-center bg-slate-50 rounded-2xl border-2 border-dashed border-slate-200">
          <div className="text-4xl mb-4">📍</div>
          <h4 className="text-lg font-semibold text-slate-700">No stores found</h4>
          <p className="text-slate-400 mb-6">Start by adding your first store</p>
          <Button 
            onClick={() => {
              const currentUserData = JSON.parse(localStorage.getItem('company_user') || '{}');
              if (currentUserData.storeRef && currentUserData.storeRef !== 'all') {
                toast.error("Invalid! Switch To All Stores and try to add a new store.");
                return;
              }
              openCreate();
            }} 
            variant="secondary" 
            size="sm"
          >
            Add Store
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {locations.map((location) => (
            <div
              key={location._id}
              className="card p-5 border border-slate-200 hover:border-brand-blue/50 transition-all group"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="w-10 h-10 bg-slate-100 rounded-xl flex items-center justify-center text-slate-700">
                  <MapPin size={20} strokeWidth={2.5} />
                </div>
                <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => openEdit(location)}
                    className="p-2 text-slate-400 hover:text-brand-blue hover:bg-brand-blue/10 rounded-lg transition-colors"
                  >
                    <Edit2 size={16} />
                  </button>
                  <button
                    onClick={() => {
                      if (!window.confirm("Are you sure you want to delete this store?")) return;
                      removeLocation(location._id);
                    }}
                    className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              <h4 className="font-medium text-slate-900 text-[15px] mb-1.5 tracking-tight flex items-center gap-2">
                {location.name}
                {location.serialNumber && (
                  <span className="px-1.5 py-0.5 bg-slate-100 text-slate-500 rounded text-[10px] font-bold uppercase tracking-wider">
                    {location.serialNumber}
                  </span>
                )}
              </h4>
              
              <p className="text-[13px] text-slate-500 mb-5 leading-relaxed font-normal tracking-tight">
                {location.addressLine1 || location.address}
                {location.addressLine2 && `, ${location.addressLine2}`}
                <br />
                {location.city}, {location.state} - {location.pincode}
              </p>

              <div className="space-y-3 pt-1">
                {location.contactName && (
                  <div className="flex items-center gap-2.5 text-[13px] text-slate-500 font-medium">
                    <User size={14} className="text-slate-400 shrink-0" />
                    {location.contactName}
                  </div>
                )}

                {location.phone && (
                  <div className="flex items-center gap-2.5 text-[14px] text-slate-700 font-bold">
                    <Phone size={14} className="text-slate-400" />
                    {location.phone}
                  </div>
                )}
                
                {location.notes && (
                  <div className="flex items-start gap-2.5 text-[13px] text-slate-500 font-medium">
                    <span className="text-slate-400 mr-1 shrink-0">Notes:</span>
                    <span className="italic">{location.notes}</span>
                  </div>
                )}

              </div>
            </div>
          ))}
        </div>
      )}

      {modalOpen && (
        <LocationModal
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
          onSave={saveLocation}
          initialData={editingLocation}
        />
      )}
    </div>
  );
}




